import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * A custom element that manages the sidemenu category drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} panel - The drawer panel.
 * @property {HTMLElement} backdrop - The backdrop overlay.
 * @property {HTMLButtonElement} closeButton - The close button.
 *
 * @extends {Component<Refs>}
 */
class SideMenu extends Component {
  requiredRefs = ['panel', 'backdrop', 'closeButton'];

  /** @type {boolean} */
  #isOpen = false;

  /** @type {HTMLButtonElement|null} */
  #triggerElement = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('sidemenu:toggle', this.#handleToggleEvent);
    this.refs.backdrop.addEventListener('click', this.#close);
    this.refs.closeButton.addEventListener('click', this.#close);
    this.addEventListener('keydown', this.#onKeyDown);

    this.querySelectorAll('.sidemenu__submenu-toggle').forEach((btn) => {
      btn.addEventListener('click', this.#handleSubmenuToggle);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('sidemenu:toggle', this.#handleToggleEvent);
    this.refs.backdrop.removeEventListener('click', this.#close);
    this.refs.closeButton.removeEventListener('click', this.#close);
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  #handleToggleEvent = () => {
    this.#triggerElement = document.querySelector('[data-sidemenu-trigger]');
    this.#isOpen ? this.#close() : this.#open();
  };

  #open = () => {
    if (this.#isOpen) return;
    this.#isOpen = true;

    this.setAttribute('aria-hidden', 'false');

    if (this.#triggerElement) {
      this.#triggerElement.setAttribute('aria-expanded', 'true');
    }

    document.documentElement.setAttribute('scroll-lock', '');

    this.refs.panel.addEventListener(
      'transitionend',
      () => trapFocus(this.refs.panel),
      { once: true }
    );
  };

  #close = () => {
    if (!this.#isOpen) return;
    this.#isOpen = false;

    this.setAttribute('aria-hidden', 'true');

    if (this.#triggerElement) {
      this.#triggerElement.setAttribute('aria-expanded', 'false');
    }

    removeTrapFocus();
    document.documentElement.removeAttribute('scroll-lock');

    if (this.#triggerElement) {
      this.#triggerElement.focus();
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#close();
    }
  };

  /**
   * Handle accordion-style submenu toggle (level 2).
   * @param {MouseEvent} event
   */
  #handleSubmenuToggle = (event) => {
    /** @type {HTMLButtonElement} */
    const button = event.currentTarget;
    const submenuId = button.getAttribute('aria-controls');
    const submenu = submenuId ? document.getElementById(submenuId) : null;

    if (!submenu) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      button.setAttribute('aria-expanded', 'false');
      submenu.hidden = true;
    } else {
      button.setAttribute('aria-expanded', 'true');
      submenu.hidden = false;
    }
  };
}

if (!customElements.get('side-menu')) {
  customElements.define('side-menu', SideMenu);
}
